import { createNamespacedHelpers } from 'vuex';
import searchPlaces from '@/queries/searchPlaces';
import apolloClient from '@/vue-apollo';
import _get from 'lodash/get';
import {
  extractPlaceParams,
  extractPlaceDestination,
  extractPlaceNameByType,
  getNavigateToDestinationParams
} from '@/utils/destinationSearchUtils';
import { getDestinationImage } from '@/utils/destinationImages';
import router from '@/router';
import { logMixPanelEvent, mixPanelEvent } from '../../utils/mixpanel';

export const namespacedHelper = createNamespacedHelpers('destination');

const state = {
  isMap: true,
  isFilterOpen: false,
  destinationInfo: {},
  destinationInfoLoading: false
};

const getters = {
  getDestinationInfo(state) {
    return state.destinationInfo;
  },
  getDestinationInfoLoading(state) {
    return state.destinationInfoLoading;
  },
  getDestinationParams(state) {
    return extractPlaceParams(state.destinationInfo);
  },
  getDestinationInfoPlace(state) {
    if (!(state.destinationInfo && state.destinationInfo.mbId)) {
      return null;
    }

    const place = extractPlaceNameByType(state.destinationInfo, 'place');

    return place && place.text;
  },
  getDestinationBreadcrumbs(state, getters) {
    if (!(state.destinationInfo && state.destinationInfo.mbId)) {
      return [];
    }

    const country =
      extractPlaceNameByType(state.destinationInfo, 'country') ||
      extractPlaceNameByType(state.destinationInfo, 'region');

    if (!country) {
      return [
        {
          text: getters.getDestinationInfoPlace,
          disabled: true
        }
      ];
    } else {
      return [
        {
          text: country.text,
          disabled: true
        },
        {
          text: getters.getDestinationInfoPlace,
          disabled: true
        }
      ];
    }
  },
  getDestinationHero(state, getters) {
    if (!(state.destinationInfo && state.destinationInfo.mbId)) {
      return {};
    }

    return {
      title: getters.getDestinationInfoPlace,
      backgroundImgSrc: getDestinationImage(getters.getDestinationInfoPlace)
    };
  },
  getDestinationFilterText(state) {
    if (!(state.destinationInfo && state.destinationInfo.mbId)) {
      return '';
    }
    return extractPlaceDestination(state.destinationInfo);
  }
};

const mutations = {
  updateMapView(state) {
    state.isMap = !state.isMap;
  },
  updateFilterOpen(state, payload) {
    state.isFilterOpen = payload;
  },
  updateDestinationInfo(state, payload) {
    state.destinationInfo = payload;
  },
  updateDestinationInfoLoading(state, payload) {
    state.destinationInfoLoading = payload;
  }
};

const actions = {
  toggleMap({ commit, rootGetters }) {
    commit('updateMapView');
    logMixPanelEvent(
      mixPanelEvent.mapView,
      {
        userId: rootGetters['user/userId'],
        activeTripId: rootGetters['user/activeTripId'],
        distinct_id: rootGetters['user/userId']
      },
      rootGetters['user/getLocationCoordinates']
    );
  },
  toggleFilter({ commit }, options) {
    commit('updateFilterOpen', options);
  },
  setDestinationInfo({ commit }, options) {
    commit('updateDestinationInfo', options);
  },
  setDestinationInfoLoading({ commit }, options) {
    commit('updateDestinationInfoLoading', options);
  },
  async searchPlaces({ commit }, options) {
    commit('updateDestinationInfoLoading', true);
    return searchPlaces({
      apollo: apolloClient.defaultClient,
      ...options
    })
      .then(response => _get(response, 'data.searchPlaces', []))
      .then(response => {
        // Keep the previous destination when the lookup resolves nothing —
        // committing undefined poisons every consumer of destinationInfo.
        if (response && response[0]) {
          commit('updateDestinationInfo', response[0]);
        }
        commit('updateDestinationInfoLoading', false);
      });
  },
  setDestination({ getters, rootGetters, commit, dispatch }, options) {
    const runSearch = opts =>
      searchPlaces({
        apollo: apolloClient.defaultClient,
        ...opts
      }).then(response => _get(response, 'data.searchPlaces', []));

    return runSearch(options)
      .then(response => {
        if (response && response[0]) {
          return response;
        }
        // Suburb-level destinations (e.g. "Chippendale") are Mapbox
        // 'locality' features and invisible to a types=place search —
        // retry once with the broader type set before giving up.
        return runSearch({ ...options, types: 'place,locality,district' });
      })
      .then(response => {
        if (!(response && response[0])) {
          commit('updateDestinationInfoLoading', false);
          return false; // caller skips the redirect; page state stays intact
        }
        commit('updateDestinationInfo', response[0]);
        if (getters.getDestinationInfo && getters.getDestinationInfo.center) {
          dispatch('filter/setCoordinates', getters.getDestinationInfo.center, {
            root: true
          });
        }
        commit('updateDestinationInfoLoading', false);
        return getNavigateToDestinationParams(
          router.currentRoute.value,
          getters.getDestinationInfo,
          {
            ...router.currentRoute.value.query,
            ...rootGetters['filter/getFilterQuery']
          }
        );
      });
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
